import React, { useState, useEffect } from 'react';
import { X, Save, Building2, Search, AlertCircle } from 'lucide-react';
import usuarioClienteService, { ClienteVinculo } from '../services/usuarioClienteService';
import clienteService from '../services/clienteService';
import { msg } from '../utils/messages';
import { cnpjMatch } from '../utils/cnpjMatch';  // Story 2.55

interface Cliente {
  id: number;
  codigo: string;
  razao_social: string;
  nome_fantasia?: string;
  cnpj?: string;
  ativo: boolean;
}

interface VincularClientesUsuarioModalProps {
  usuarioId: number;
  usuarioNome: string;
  usuarioRole: string;
  onClose: () => void;
  onSave: () => void;
}

const VincularClientesUsuarioModal: React.FC<VincularClientesUsuarioModalProps> = ({
  usuarioId,
  usuarioNome,
  usuarioRole,
  onClose,
  onSave,
}) => {
  const [todosClientes, setTodosClientes] = useState<Cliente[]>([]);
  const [clientesSelecionados, setClientesSelecionados] = useState<number[]>([]);
  const [clientesOriginais, setClientesOriginais] = useState<number[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoadingData(true);

      // Carregar todos os clientes
      const clientes = await clienteService.listClientes();
      setTodosClientes(clientes.filter((c: Cliente) => c.ativo));

      // Carregar clientes ja vinculados ao usuario
      const vinculados = await usuarioClienteService.getClientesUsuario(usuarioId);
      const idsVinculados = vinculados.map((v: ClienteVinculo) => v.id);
      setClientesSelecionados(idsVinculados);
      setClientesOriginais(idsVinculados);

    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      msg.error('Erro ao carregar dados');
    } finally {
      setLoadingData(false);
    }
  };

  const handleToggleCliente = (clienteId: number) => {
    if (clientesSelecionados.includes(clienteId)) {
      setClientesSelecionados(clientesSelecionados.filter(id => id !== clienteId));
    } else {
      setClientesSelecionados([...clientesSelecionados, clienteId]);
    }
  };

  const handleToggleAll = () => {
    const filtrados = filteredClientes();
    const todosIds = filtrados.map(c => c.id);
    const todosSelecionados = todosIds.every(id => clientesSelecionados.includes(id));

    if (todosSelecionados) {
      setClientesSelecionados(clientesSelecionados.filter(id => !todosIds.includes(id)));
    } else {
      const idsExistentes = clientesSelecionados.filter(id => !todosIds.includes(id));
      setClientesSelecionados([...idsExistentes, ...todosIds]);
    }
  };

  const filteredClientes = () => {
    if (!searchTerm) return todosClientes;

    const term = searchTerm.toLowerCase();
    return todosClientes.filter(c =>
      c.razao_social?.toLowerCase().includes(term) ||
      c.nome_fantasia?.toLowerCase().includes(term) ||
      c.codigo?.toLowerCase().includes(term) ||
      // Story 2.55: busca por CNPJ normalizada (ignora ./- /)
      cnpjMatch(c.cnpj, searchTerm)
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Verificar se houve mudancas
    const novos = clientesSelecionados.filter(id => !clientesOriginais.includes(id));
    const removidos = clientesOriginais.filter(id => !clientesSelecionados.includes(id));

    if (novos.length === 0 && removidos.length === 0) {
      msg.info('Nenhuma alteracao foi feita');
      return;
    }

    try {
      setLoading(true);

      // Usar bulk update para substituir todos os vinculos
      await usuarioClienteService.bulkUpdateVinculos(usuarioId, clientesSelecionados);

      const msgParts = [];
      if (novos.length > 0) msgParts.push(`${novos.length} adicionado(s)`);
      if (removidos.length > 0) msgParts.push(`${removidos.length} removido(s)`);

      msg.success(`Vinculos atualizados: ${msgParts.join(', ')}`);
      onSave();

    } catch (error) {
      console.error('Erro ao salvar vinculos:', error);
      msg.error('Erro ao salvar vinculos');
    } finally {
      setLoading(false);
    }
  };

  const filtrados = filteredClientes();
  const todosSelecionadosNaLista = filtrados.length > 0 &&
    filtrados.every(c => clientesSelecionados.includes(c.id));

  // Roles que precisam de vinculos
  const rolesNecessitamVinculo = ['CLIENTE', 'CONFERENTE', 'OPERATOR', 'ANALISTA'];
  const precisaVinculo = rolesNecessitamVinculo.includes(usuarioRole);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              Vincular Clientes
            </h2>
            <p className="text-sm text-gray-500">
              Usuario: {usuarioNome} ({usuarioRole})
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Aviso para roles que precisam de vinculo */}
        {precisaVinculo && clientesSelecionados.length === 0 && (
          <div className="mx-4 mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start gap-2">
            <AlertCircle className="text-yellow-600 flex-shrink-0 mt-0.5" size={18} />
            <div className="text-sm text-yellow-800">
              <strong>Atencao:</strong> Usuarios com perfil {usuarioRole} precisam de pelo menos um cliente vinculado para acessar o sistema.
            </div>
          </div>
        )}

        {/* Search */}
        <div className="p-4 border-b">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              placeholder="Buscar cliente..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {loadingData ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : filtrados.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Building2 className="mx-auto mb-2" size={48} />
              <p>Nenhum cliente encontrado</p>
            </div>
          ) : (
            <div className="space-y-2">
              {/* Select All */}
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border">
                <input
                  type="checkbox"
                  checked={todosSelecionadosNaLista}
                  onChange={handleToggleAll}
                  className="h-4 w-4 text-blue-600 rounded focus:ring-blue-500"
                />
                <span className="font-medium text-sm text-gray-700">
                  Selecionar todos ({filtrados.length})
                </span>
              </div>

              {/* Client List */}
              {filtrados.map((cliente) => (
                <label
                  key={cliente.id}
                  className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                    clientesSelecionados.includes(cliente.id)
                      ? 'bg-blue-50 border-blue-300'
                      : 'bg-white border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={clientesSelecionados.includes(cliente.id)}
                    onChange={() => handleToggleCliente(cliente.id)}
                    className="h-4 w-4 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-gray-900 truncate">
                      {cliente.nome_fantasia || cliente.razao_social}
                    </div>
                    <div className="text-sm text-gray-500">
                      {cliente.codigo}
                      {cliente.cnpj && ` - ${cliente.cnpj}`}
                    </div>
                  </div>
                  {clientesOriginais.includes(cliente.id) && (
                    <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">
                      Vinculado
                    </span>
                  )}
                </label>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t bg-gray-50">
          <div className="text-sm text-gray-500">
            {clientesSelecionados.length} cliente(s) selecionado(s)
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancelar
            </button>
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:bg-blue-400 transition-colors"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Salvando...
                </>
              ) : (
                <>
                  <Save size={16} />
                  Salvar
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VincularClientesUsuarioModal;
